import { Decimal } from '../../datastructures';
import { configAtom } from '../configAtom';
import assetsAtom from './assetsAtom';
import assetTokenRatesAtom from './assetTokenRatesAtom';
import lendPositionsAtom from './lendPositionsAtom';
import debtPositionsAtom from './debtPositionsAtom';
import { createAsyncAtom } from '../../lib/state';

const activeMarketsFilterAtom = createAsyncAtom<string>(async () => 'All');

const filteredMarketsAtom = createAsyncAtom(async get => {
  const config = await get(configAtom);
  const activeMarketsFilter = await get(activeMarketsFilterAtom);
  const assets = await get(assetsAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);
  const lendPositions = await get(lendPositionsAtom);
  const debtPositions = await get(debtPositionsAtom);

  if (!assets) {
    return [];
  }

  const filteredMarkets = assets.filter(asset => {
    // If asset is hidden and user does not have a position in it, do not show it
    if (asset.hidden) {
      const hiddenAssetLendPosition = lendPositions?.find(position => position.asset.address === asset.address);
      const hiddenAssetDebtPosition = debtPositions?.find(position => position.asset.address === asset.address);

      if (!hiddenAssetLendPosition && !hiddenAssetDebtPosition) {
        return false;
      }
    }

    if (activeMarketsFilter === 'All') {
      return true;
    }

    return activeMarketsFilter.toLowerCase() === asset.assetTier;
  });

  const sortedAssets = filteredMarkets.sort((assetA, assetB) => {
    const assetABoostedPoints = config.assets[assetA.token].pointsBoostFinishTimestamp > Date.now();
    const assetBBoostedPoints = config.assets[assetB.token].pointsBoostFinishTimestamp > Date.now();

    const assetATokenRate = assetTokenRates.rate[assetA.address] ?? Decimal.ZERO;
    const assetBTokenRate = assetTokenRates.rate[assetB.address] ?? Decimal.ZERO;
    const assetAAvailableForBorrowing = assetA.availableForBorrowing ?? Decimal.ZERO;
    const assetBAvailableForBorrowing = assetB.availableForBorrowing ?? Decimal.ZERO;
    let assetAAvailableForBorrowingInUsd = assetAAvailableForBorrowing.mul(assetATokenRate);
    let assetBAvailableForBorrowingInUsd = assetBAvailableForBorrowing.mul(assetBTokenRate);

    // sort by rewards > point boost > normal > experimental, and then by tvl
    if (assetA.defiSpringRewardsApy?.gt(0)) {
      assetAAvailableForBorrowingInUsd = assetAAvailableForBorrowingInUsd.add(new Decimal(10n ** 40n, 1));
    }
    if (assetB.defiSpringRewardsApy?.gt(0)) {
      assetBAvailableForBorrowingInUsd = assetBAvailableForBorrowingInUsd.add(new Decimal(10n ** 40n, 1));
    }
    if (assetABoostedPoints) {
      assetAAvailableForBorrowingInUsd = assetAAvailableForBorrowingInUsd.add(new Decimal(10n ** 20n, 1));
    }
    if (assetBBoostedPoints) {
      assetBAvailableForBorrowingInUsd = assetBAvailableForBorrowingInUsd.add(new Decimal(10n ** 20n, 1));
    }

    return assetAAvailableForBorrowingInUsd.sub(assetBAvailableForBorrowingInUsd).gt(0) ? -1 : 1;
  });

  return sortedAssets;
});

export default filteredMarketsAtom;

export { activeMarketsFilterAtom };
